/**
 * tagParser.ts（F-M13 拆分后的 re-export 入口）
 *
 * 🔵 F-M13: 原 351 行文件已按职责拆分为 3 个模块：
 *   - utils/tagRegex.ts        标签正则常量
 *   - utils/tagExtractor.ts    extract* 函数
 *   - utils/processLogFactory.ts createProcessLog / finalizeProcessLogs / addCollabStatusToLogs
 *
 * 本文件保留作为向后兼容的 re-export 入口，14 个 import 该文件的模块无需修改。
 * 仅保留 agent 元信息、STATUS_ICONS、GENERATION_FEATURE_MAP、calculateNewNodePosition 等
 * 不属于上述 3 类职责的导出。
 */
import type { AgentProfile } from '@/types/agent';
import { useAgentStore } from '@/stores/agentStore';

// ============================================================================
// Re-export：3 个拆分模块的公共 API
// ============================================================================

// 1. 标签正则常量
export {
  THINK_TAG_RE,
  TOOL_CALL_TAG_RE,
  CALL_AGENT_TAG_RE,
  CALL_AGENT_ASYNC_TAG_RE,
  COLLABORATION_RESULT_TAG_RE,
  COLLAB_STATUS_TAG_RE,
  TAG_PATTERN,
  ALT_TAG_PATTERN,
} from './utils/tagRegex';

// 2. ProcessLog 工厂与去重
export {
  createLocalId,
  createProcessLog,
  finalizeProcessLogs,
  addCollabStatusToLogs,
} from './utils/processLogFactory';

// 3. 标签提取函数
export {
  extractAllContentFromText,
  extractProcessLogsFromText,
  parseCollabStatusTag,
  stripThinkingAndToolCallTags,
  type AgentCallInfo,
  type CollaborationResultItem,
  type ExtractedContent,
} from './utils/tagExtractor';
import { _setStatusIcons } from './utils/tagExtractor';

// ============================================================================
// 仍由 tagParser.ts 直接持有：协作阶段图标
// ============================================================================

export const STATUS_ICONS: Record<string, string> = {
  calling: '📡',
  accepted: '🤝',
  executing: '⚡',
  completed: '✅',
  handoff: '🔄',
  returning: '🔄',
  error: '❌',
  searching: '🔍',
  running: '🟡',
  failed: '❌',
  rejected: '❌',
  timeout: '❌',
  pending: '⏱️',
  cancelled: '⏱️',
  clarifying: '💬',
};

// F-M13: 把 STATUS_ICONS 注入 tagExtractor，保证两处一致
_setStatusIcons(STATUS_ICONS);

// ============================================================================
// 仍由 tagParser.ts 直接持有：Agent 元信息
// ============================================================================

const DEFAULT_AGENT_COLOR = '#6366f1';
const DEFAULT_AGENT_AVATAR = '🤖';

const _getAgentProfile = (agentId: string): AgentProfile | undefined => {
  return useAgentStore.getState().agents.find(a => a.id === agentId);
};

export const hasAgentProfile = (agentId: string): boolean => {
  return _getAgentProfile(agentId) !== undefined;
};

export const getAgentName = (agentId: string): string => {
  const profile = _getAgentProfile(agentId);
  if (profile) return profile.name;
  return agentId.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export interface AgentAvatarInfo {
  color: string;
  emoji: string;
}

// P2-24/P2-33: 原 getAgentAvatar/getAgentColor 已内联到 getAgentAvatarInfo，
// 单次查询 _getAgentProfile，避免 2 次 store 查找
export const getAgentAvatarInfo = (agentId: string): AgentAvatarInfo => {
  const profile = _getAgentProfile(agentId);
  return {
    color: profile?.color || DEFAULT_AGENT_COLOR,
    emoji: profile?.avatar || DEFAULT_AGENT_AVATAR,
  };
};

export const DEFAULT_AVATAR_INFO: AgentAvatarInfo = { color: DEFAULT_AGENT_COLOR, emoji: DEFAULT_AGENT_AVATAR };

export const formatAgentIdToName = getAgentName;

// ============================================================================
// 仍由 tagParser.ts 直接持有：生成功能映射 / 节点位置计算
// ============================================================================

export const GENERATION_FEATURE_MAP: Record<string, string> = {
  '多机位九宫格': '基于一张参考图生成3×3不同机位/景别的关键帧，画面中不得出现任何文字、序号、编号、水印',
  '剧情推演四宫格': '基于一张参考图生成2×2时间线（生5s/3s）动态演变帧，画面中不得出现任何文字、序号、编号、水印',
  '25宫格连贯分镜': '基于一张图生成连贯的25宫格分镜（5*5），画面中不得出现任何文字、序号、编号、水印',
  '电影级光影校正': '一键仅调整光照/曝光/色温，不改变画面内容',
  '角色三视图生成': '一键生成角色三视图（正面/侧面/背面），画面中不得出现任何文字、序号、编号、水印',
  // P2-28: 统一为带空格版本（与 FEATURE_GRIDS / TopFunctionBar 标签一致）
  '画面推演 - 3秒后': '基于物理逻辑，生成3秒后的动作结果，画面中不得出现任何文字、序号、编号、水印',
  '画面推演 - 5秒前': '基于物理逻辑，反推5秒前的动作起因，画面中不得出现任何文字、序号、编号、水印',
};

/** 节点短边像素（与 GenerateNode 的 SHORT_EDGE 保持一致） */
const NODE_SHORT_EDGE = 420;
/** 批量节点之间的额外间距（含上方标题区域约 33px + 视觉缓冲） */
const BATCH_GAP = 80;

/**
 * 根据节点 height / aspectRatio 计算实际渲染高度。
 * 公式与 GenerateNode.tsx 的 cardHeight 一致：短边恒 420，长边按比例缩放。
 *   横屏/方形 (w>=h): height = 420
 *   竖屏     (w< h): height = 420 × h/w  （如 9:16→747, 1:3→1260）
 */
export function getNodeRenderHeight(height?: number, aspectRatio?: string): number {
  if (height) return height;
  const ratio = aspectRatio || '16:9';
  const [w, h] = ratio.split(':').map(Number);
  if (!w || !h) return NODE_SHORT_EDGE;
  const isLandscape = w >= h;
  return isLandscape ? NODE_SHORT_EDGE : (NODE_SHORT_EDGE * h) / w;
}

export const calculateNewNodePosition = (
  existingNodes: Array<{ x: number; y: number; height?: number; data?: { aspectRatio?: string } }>,
  sourceNode?: { x: number; y: number } | null,
  options?: { batchVertical?: boolean },
): { x: number; y: number } => {
  // 批量创建：节点上下垂直排列，行距根据上一节点的实际渲染高度动态计算，确保任何节点区域不重叠
  if (options?.batchVertical && existingNodes.length > 0) {
    const lastNode = existingNodes[existingNodes.length - 1];
    const lastHeight = getNodeRenderHeight(lastNode.height, lastNode.data?.aspectRatio);
    return { x: lastNode.x, y: (lastNode.y ?? 120) + lastHeight + BATCH_GAP };
  }
  if (sourceNode) {
    return { x: sourceNode.x + 500, y: sourceNode.y };
  }
  if (existingNodes.length > 0) {
    return {
      x: Math.max(...existingNodes.map((node) => node.x)) + 380,
      y: existingNodes[existingNodes.length - 1]?.y ?? 120,
    };
  }
  return { x: 120, y: 120 };
};
